'use client';

import { useDebouncedState } from '@mantine/hooks';
import * as Popover from '@radix-ui/react-popover';
import { usePathname } from 'next/navigation';
import { useMemo } from 'react';
import { twJoin } from 'tailwind-merge';

import { Button } from '@/components/Button';
import { clsx, doesRouteMatch } from '@/utils/helpers';
import { Paths } from '@/utils/paths';

import { SolutionsMenuItem } from './SolutionsMenuItem';

export type SolutionsMenuProps = {
  content: LayoutContent;
};

export function SolutionsMenu({ content }: SolutionsMenuProps) {
  const [isOpen, setIsOpen] = useDebouncedState(false, 200);

  const pathname = usePathname();

  const { solutions } = content.topbar;

  const isActive = useMemo(() => {
    const paths = [Paths.STORES, Paths.BENEFITS, Paths.NETWORKS];

    const activeMenu = paths.find(path => {
      return doesRouteMatch({ signature: path, route: pathname });
    });

    return Boolean(activeMenu);
  }, [pathname]);

  function onMouseEnter() {
    setIsOpen(true);
  }

  function onMouseLeave() {
    setIsOpen(false);
  }

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        <Button
          className={clsx(
            'font-medium',
            'hover:text-zmyle-primary',
            isActive && 'text-zmyle-primary',
          )}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          variant="ghost"
        >
          {solutions}
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          sideOffset={12}
          className={twJoin(
            'relative z-15 flex w-48 flex-col gap-2 py-2',
            'rounded-lg bg-white shadow-lg',
            'border border-gray-100',
            'outline-none',
          )}
          side="bottom"
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <Popover.Arrow
            className="fill-white stroke-gray-100 stroke-1"
            width={15}
            height={10}
          />

          <SolutionsMenuItem content={content} type="store" />

          <SolutionsMenuItem content={content} type="benefit" />

          <SolutionsMenuItem content={content} type="network" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
